import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIsAuthenticated } from '@azure/msal-react';
import { differenceInDays, parseISO, startOfDay, format } from 'date-fns';
import { pedService } from '../services/endpoints/pedService';
import { metricsService } from '../services/endpoints/metricsService';
import messageService from '../services/endpoints/messageService';
import { getAthleteAssignments } from '../services/endpoints/trainingService';
import { planFileService } from '../services/endpoints/planFileService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { AppAlert, CoachPlanFile, PEDCycle, UnreadMessageSummary } from '../types';
import type { AthleteAssignmentDetail } from '../types/training';

// ── Context shape ─────────────────────────────────────────────────────────────

interface AlertContextValue {
  alerts: AppAlert[];
  unackedCount: number;
  acknowledge: (alertId: string) => void;
  isBellOpen: boolean;
  setIsBellOpen: (open: boolean) => void;
}

const AlertContext = createContext<AlertContextValue>({
  alerts: [],
  unackedCount: 0,
  acknowledge: () => {},
  isBellOpen: false,
  setIsBellOpen: () => {},
});

export const useAlerts = () => useContext(AlertContext);

// ── Provider ──────────────────────────────────────────────────────────────────

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();
  const [isBellOpen, setIsBellOpen] = useState(false);

  // localStorage key scoped to user so different accounts don't share ack state
  const storageKey = user
    ? `showtracker-alert-acks-${user.userId}`
    : 'showtracker-alert-acks';

  const [ackedIds, setAckedIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  // Re-read from localStorage when the user changes (login/logout)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      setAckedIds(stored ? new Set<string>(JSON.parse(stored)) : new Set<string>());
    } catch {
      setAckedIds(new Set<string>());
    }
  }, [storageKey]);

  const today = startOfDay(new Date());
  const todayStr = format(today, 'yyyy-MM-dd');

  // ── Fetch today's metrics to determine if user has checked in ─────────────
  // Returns null if no metrics logged today (404 or empty), otherwise the record.
  const { data: todayMetrics, isPending: metricsPending } = useQuery({
    queryKey: ['metrics-today-check', todayStr],
    queryFn: async () => {
      try {
        return await metricsService.getMetricsByDate(todayStr);
      } catch {
        return null; // 404 or any error → treat as "not logged yet"
      }
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,   // consider fresh for 5 min
    refetchOnWindowFocus: true,  // re-check when user tabs back in
  });

  // ── Fetch upcoming PED cycles for order reminders ─────────────────────────
  const { data: cycles = [] } = useQuery<PEDCycle[]>({
    queryKey: ['ped-cycles'],
    queryFn: () => pedService.getCycles(),
    enabled: !!user,
  });

  // ── Poll for unread messages (every 60 s) ─────────────────────────────────
  const { data: unreadSummaries = [] } = useQuery<UnreadMessageSummary[]>({
    queryKey: ['unread-messages'],
    queryFn: () => messageService.getUnreadSummary(),
    enabled: isAuthenticated,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // ── Poll athlete training assignments (every 2 min) ───────────────────────
  // Works for any user: coaches with no assignments get [] and no alerts fire.
  const { data: athleteAssignments = [] } = useQuery<AthleteAssignmentDetail[]>({
    queryKey: ['athlete-assignments-for-alerts'],
    queryFn: () => getAthleteAssignments(),
    enabled: isAuthenticated,
    refetchInterval: 2 * 60_000,
    staleTime: 60_000,
  });

  // ── Poll plan files shared with this athlete (every 2 min) ───────────────
  const { data: planFiles = [] } = useQuery<CoachPlanFile[]>({
    queryKey: ['plan-files-for-alerts'],
    queryFn: () => planFileService.listMine(),
    enabled: isAuthenticated,
    refetchInterval: 2 * 60_000,
    staleTime: 60_000,
  });

  // On first load for this user, pre-ack all current training alert IDs so that
  // existing assignments don't generate false "new" notifications.
  const trainingSeedKey = user ? `showtracker-training-seeded-${user.userId}` : null;
  useEffect(() => {
    if (!trainingSeedKey || athleteAssignments.length === 0) return;
    if (localStorage.getItem(trainingSeedKey)) return; // already seeded
    const idsToSeed: string[] = [];
    for (const a of athleteAssignments) {
      // Self-coached plans never generate coach notifications — skip seeding
      if (a.isSelfCoached) continue;
      idsToSeed.push(`training-assigned-${a.assignmentId}`);
      idsToSeed.push(`training-updated-${a.assignmentId}-${a.plan.updatedAt}`);
    }
    setAckedIds(prev => {
      const next = new Set(prev);
      idsToSeed.forEach(id => next.add(id));
      try { localStorage.setItem(storageKey, JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
    localStorage.setItem(trainingSeedKey, '1');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteAssignments, trainingSeedKey]);

  // ── Seed existing plan files on first load ───────────────────────────────
  // Prevents files uploaded before this feature shipped from firing false alerts.
  const planFileSeedKey = user ? `showtracker-planfiles-seeded-${user.userId}` : null;
  useEffect(() => {
    if (!planFileSeedKey || planFiles.length === 0) return;
    if (localStorage.getItem(planFileSeedKey)) return;
    const idsToSeed = planFiles.map(f => `plan-file-shared-${f.planFileId}`);
    setAckedIds(prev => {
      const next = new Set(prev);
      idsToSeed.forEach(id => next.add(id));
      try { localStorage.setItem(storageKey, JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
    localStorage.setItem(planFileSeedKey, '1');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFiles, planFileSeedKey]);

  // ── Order-reminder alerts ─────────────────────────────────────────────────

  const orderAlerts: AppAlert[] = useMemo(() => {
    return cycles
      .filter((cycle) => {
        const start = parseISO(cycle.startDate);
        const daysUntil = differenceInDays(start, today);
        return daysUntil >= 0 && daysUntil <= 30;
      })
      .map((cycle) => {
        const start = parseISO(cycle.startDate);
        const daysUntil = differenceInDays(start, today);
        return {
          alertId: `order-reminder-${cycle.cycleId}`,
          type: 'OrderReminder' as const,
          title: `Order Reminder — ${cycle.name}`,
          body:
            daysUntil === 0
              ? `Your cycle starts today (${format(start, 'MMM d')}). Make sure you have your compounds ready!`
              : `Starts in ${daysUntil} day${daysUntil !== 1 ? 's' : ''} (${format(start, 'MMM d')}). Order your compounds now so they arrive in time.`,
          cycleId: cycle.cycleId,
          daysUntilStart: daysUntil,
          acknowledged: false,
        };
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycles, todayStr]);

  // ── Daily check-in reminder ───────────────────────────────────────────────
  // Shows as long as no metrics record exists for today.
  // Wait for the metrics query to resolve before deciding (avoids false flash).

  const checkInAlert: AppAlert | null = useMemo(() => {
    if (!isAuthenticated) return null;
    if (metricsPending) return null;
    if (todayMetrics != null) return null;
    return {
      alertId: `daily-checkin-${todayStr}`,
      type: 'DailyCheckInReminder' as const,
      title: "Log Today's Check-In",
      body: "You haven't logged your weight and vitals yet today. Tap to log now.",
      acknowledged: false,
    };
  }, [isAuthenticated, metricsPending, todayMetrics, todayStr]);

  // ── NewMessage alerts from unread summaries ───────────────────────────────
  // Each distinct sender×relationship×contextType becomes one bell entry.
  const messageAlerts: AppAlert[] = useMemo(() => {
    return unreadSummaries.map((s) => {
      const ctxLabel =
        s.contextType === 'DailyCheckIn' ? ' on a check-in'
        : s.contextType === 'PEDCycle'   ? ' on a cycle'
        : '';
      return {
        alertId:        `new-message-${s.relationshipId}-${s.senderUserId}-${s.contextType ?? 'General'}-${s.contextId ?? ''}`,
        type:           'NewMessage' as const,
        title:          `${s.unreadCount} new note${s.unreadCount !== 1 ? 's' : ''} from ${s.senderName}${ctxLabel}`,
        body:           s.latestSnippet,
        relationshipId: s.relationshipId,
        contextType:    s.contextType,
        contextId:      s.contextId,
        senderUserId:   s.senderUserId,
        senderName:     s.senderName,
        senderIsCoach:  s.senderIsCoach,
        acknowledged:   false,
      };
    });
  }, [unreadSummaries]);

  // ── Training plan assignment / update alerts ──────────────────────────────
  // Alerts are only included when NOT already acked, so they vanish on ack
  // without leaving a dimmed ghost in the bell.
  // Self-coached plans are excluded entirely — the athlete manages their own
  // plan so no coach-origin "New Plan Assigned" or "Plan Updated" makes sense.
  const trainingAlerts: AppAlert[] = useMemo(() => {
    return athleteAssignments.flatMap(a => {
      // Skip self-coached assignments — athlete is their own coach
      if (a.isSelfCoached) return [];

      const alerts: AppAlert[] = [];
      const assignedId = `training-assigned-${a.assignmentId}`;
      if (!ackedIds.has(assignedId)) {
        alerts.push({
          alertId: assignedId,
          type:    'TrainingPlanAssigned',
          title:   'New Training Plan Assigned',
          body:    `Your coach assigned you the "${a.plan.name}" training plan.`,
          acknowledged: false,
        });
      }
      // Plan-updated alert: fires whenever plan.updatedAt differs from plan.createdAt
      // (i.e. the plan has been edited at least once after creation).
      // Using updatedAt in the alertId ensures a fresh alert each time the plan changes.
      // Fresh copies always have createdAt == updatedAt at assignment time, so this
      // naturally fires only after a real edit — no need for an ackedIds guard.
      if (a.plan.updatedAt !== a.plan.createdAt) {
        const updatedId = `training-updated-${a.assignmentId}-${a.plan.updatedAt}`;
        if (!ackedIds.has(updatedId)) {
          alerts.push({
            alertId: updatedId,
            type:    'TrainingPlanUpdated',
            title:   'Training Plan Updated',
            body:    `Your training plan "${a.plan.name}" has been updated by your coach.`,
            acknowledged: false,
          });
        }
      }
      return alerts;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteAssignments, ackedIds]);

  // ── Plan file shared alerts ───────────────────────────────────────────────
  const planFileAlerts: AppAlert[] = useMemo(() => {
    return planFiles.flatMap(f => {
      const alertId = `plan-file-shared-${f.planFileId}`;
      if (ackedIds.has(alertId)) return [];
      return [{
        alertId,
        type:    'PlanFileShared' as const,
        title:   'New Plan File',
        body:    `${f.coachName} shared "${f.originalFileName}" with you.`,
        acknowledged: false,
      }];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFiles, ackedIds]);

  // ── Combine all alerts ────────────────────────────────────────────────────

  const rawAlerts: AppAlert[] = useMemo(() => {
    // Training + plan file alerts first (highest relevance), then messages, check-in, order reminders
    const all: AppAlert[] = [...trainingAlerts, ...planFileAlerts, ...messageAlerts, ...orderAlerts];
    if (checkInAlert) all.unshift(checkInAlert);
    return all;
  }, [trainingAlerts, planFileAlerts, messageAlerts, orderAlerts, checkInAlert]);

  // Merge raw alerts with ack state.
  // NewMessage alerts are NEVER persisted to localStorage — they clear
  // automatically once the server marks messages as read on the next poll.
  const alerts: AppAlert[] = useMemo(() => {
    const priorityOf = (a: AppAlert) =>
      a.type === 'DailyCheckInReminder' ? -1
      : a.type === 'NewMessage'         ?  0
      :                                   (a.daysUntilStart ?? 99);
    return rawAlerts
      .map((alert: AppAlert) => ({
        ...alert,
        // NewMessage alerts are never persisted-acked; they clear via poll
        acknowledged: alert.type === 'NewMessage' ? false : ackedIds.has(alert.alertId),
      }))
      .sort((a: AppAlert, b: AppAlert) => priorityOf(a) - priorityOf(b));
  }, [rawAlerts, ackedIds]);

  const unackedCount = alerts.filter((a) => !a.acknowledged).length;

  const acknowledge = useCallback(
    (alertId: string) => {
      setAckedIds((prev) => {
        const next = new Set(prev);
        next.add(alertId);
        try {
          localStorage.setItem(storageKey, JSON.stringify(Array.from(next)));
        } catch {
          // localStorage unavailable — ignore
        }
        return next;
      });
    },
    [storageKey]
  );

  return (
    <AlertContext.Provider value={{ alerts, unackedCount, acknowledge, isBellOpen, setIsBellOpen }}>
      {children}
    </AlertContext.Provider>
  );
};
